/**
 * @file GridCountField.tsx
 * @description «В ряд на ПК» числом у сеток элементов секции (phase19.16,
 * партия SB120, этап 3б; uiux-sb120-stage3 §3 «Плитки и категории», «Сетки
 * остальных видов», §8 «Создаём»): ползунок с подсказками ([2][3][4][6] у
 * плиток и категорий, [2][3][4] у остальных), под ним «на планшете N» — по
 * правилу витрины для вида (`GRID_META`). Пределы — набор shared, который
 * передаёт панель (`GRID_PER_ROW`, `TILE_GRID_COLS`, `REVIEW_PER_ROW`):
 * значение остаётся литералом схемы без приведения типа.
 *
 * «↺» — умолчание вида (`fallback`): у обязательного поля пишется числом, у
 * необязательного (статьи, отзывы) снимает ключ — «как было». Ползунок —
 * живой предпросмотр `…perRow` / `…cols`. Предупреждение shared о 7–8 в ряд
 * (`grid_cols_many`) — под полем.
 */

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { FieldHint } from "../../../ui/settings-groups";
import { ElementWarningNote } from "../fields/ElementWarningNote";
import { useSectionForm } from "../section-form-context";
import { GRID_META, type GridKind } from "../../../../lib/site-builder/grid-meta";
import { ownValue } from "../../../../lib/site-fields/number-input";

interface Props<N extends number> {
  kind: GridKind;
  /** Допустимые значения по возрастанию (набор shared). */
  values: readonly N[];
  /** Своё значение; `undefined` — нет ключа (только у необязательных). */
  value: N | undefined;
  /** Умолчание вида: «↺» и показ при отсутствии ключа. */
  fallback: N;
  onChange: (value: N | undefined) => void;
  /** Путь элемента. */
  path: string;
  /** Ключ поля в элементе. */
  field: "perRow" | "cols";
  /** Подпись «↺»: «как было · 3»; нет — «по умолчанию N». */
  inheritLabel?: string;
  /** Пояснение вместо строки планшета (отзывы лентой). */
  hint?: string;
}

export function GridCountField<N extends number>({ kind, values, value, fallback, onChange, path, field, inheritLabel, hint }: Props<N>) {
  const { preview } = useSectionForm();
  const meta = GRID_META[kind];
  const min = values[0] ?? 1;
  const max = values[values.length - 1] ?? min;
  const shown = value ?? fallback;
  // Число с ползунка — из того же набора; чужое (не целое) не пишем.
  const pick = (n: number | undefined): N | undefined => (n === undefined ? undefined : values.find((v) => v === n));
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <NumberSliderField
        label="В ряд на ПК"
        integer
        value={value === undefined ? undefined : ownValue(value, fallback)}
        inherit={{ value: fallback, label: inheritLabel ?? `по умолчанию ${fallback}` }}
        min={min}
        max={max}
        step={1}
        unit=""
        presets={meta.hints.filter((n) => n >= min && n <= max).map((n) => ({ label: String(n), value: n }))}
        onChange={(n) => {
          if (n === undefined) return onChange(undefined);
          const next = pick(n);
          if (next !== undefined) onChange(next);
        }}
        onPreview={(n) => preview(`${path}.${field}`, n ?? fallback)}
      />
      <FieldHint className="mt-0">{hint ?? `На планшете ${meta.tablet(shown)} в ряд.`}</FieldHint>
      <ElementWarningNote path={`${path}.${field}`} />
    </div>
  );
}
